// Products Management Component
import React, { useState, useEffect } from "react";
import FeatureGuard from "../common/FeatureGuard";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  MoreVert,
  Inventory,
  Refresh,
} from "@mui/icons-material";
import apiService from "../../services/api";
import authService from "../../services/auth";
import { useCurrency } from "../../contexts/CurrencyContext";
import { STATUS_OPTIONS } from "../../constants";
import SearchableDropdown from "../common/SearchableDropdown";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(''); // New state for success messages
  const [dialogError, setDialogError] = useState(''); // New state for dialog errors
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // all, active, inactive
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);

  // Use currency context for proper currency formatting
  const { currencySymbol, formatPrice } = useCurrency();

  const isAdminLike = (user) => String(user?.role_name || "").toLowerCase().includes("admin");

  const hasPermission = (module, action = "view") => {
    const user = authService.getUser();
    if (isAdminLike(user)) return true;
    if (!userPermissions) return false;
    return userPermissions?.[module]?.[action] === true;
  };

  const fetchPermissions = async () => {
    try {
      const user = authService.getUser();
      if (!user?.id) return;
      const response = await apiService.get(`/roles/user/${user.id}/permissions`);
      setUserPermissions(response.data || {});
    } catch (e) {
      // safest fallback: no permissions
      setUserPermissions({});
    }
  };

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    brand_id: "",
    price: "",
    status: STATUS_OPTIONS.ACTIVE,
    gst_percentage: "",
    price_inclusive_gst: false,
  });

  useEffect(() => {
    fetchData();
    fetchPermissions();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, brandsRes] = await Promise.all([
        apiService.get("/products"),
        apiService.get("/brands?status=active"), // Only get active brands for product forms
      ]);

      if (productsRes.success) setProducts(productsRes.data.products || []);
      if (brandsRes.success) setBrands(brandsRes.data.brands || []);
    } catch (err) {
      setError("Error fetching data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const filteredProducts = (products || []).filter((product) => {
    const brand = (brands || []).find((b) => b.id === product.brand_id);
    const brandName = brand ? brand.name : "";

    // Filter by status first
    const statusMatch = statusFilter === "all" || 
                       (statusFilter === "active" && product.status === STATUS_OPTIONS.ACTIVE) ||
                       (statusFilter === "inactive" && product.status === STATUS_OPTIONS.INACTIVE);

    // Then filter by search term
    const searchMatch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       brandName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (product.description &&
                         product.description.toLowerCase().includes(searchTerm.toLowerCase()));

    return statusMatch && searchMatch;
  });

  const handleOpenDialog = (product = null) => {
    // Permission checks (UI-level). API also enforces this.
    if (product && !hasPermission("products", "edit")) {
      setError("Permission denied");
      return;
    }
    if (!product && !hasPermission("products", "add")) {
      setError("Permission denied");
      return;
    }

    setDialogError(""); // Clear any existing dialog errors
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || "",
        brand_id: product.brand_id,
        price: product.price,
        status: product.status,
        gst_percentage: product.gst_percentage || "",
        price_inclusive_gst: product.price_inclusive_gst || false,
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: "",
        description: "",
        brand_id: "",
        price: "",
        status: STATUS_OPTIONS.ACTIVE,
        gst_percentage: "",
        price_inclusive_gst: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    setDialogError(""); // Clear dialog errors when closing
  };

  const handleInputChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleCheckboxChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSubmit = async () => {
    try {
      setDialogError(""); // Clear previous dialog errors

      // Permission checks (UI-level). API also enforces this.
      if (editingProduct && !hasPermission("products", "edit")) {
        setDialogError("Permission denied");
        return;
      }
      if (!editingProduct && !hasPermission("products", "add")) {
        setDialogError("Permission denied");
        return;
      }
      
      // Prepare data for submission - convert empty strings to null for optional fields
      const submitData = {
        ...formData,
        brand_id: formData.brand_id === "" ? null : formData.brand_id,
        description: formData.description === "" ? null : formData.description,
        gst_percentage: formData.gst_percentage === "" ? null : formData.gst_percentage,
        price_inclusive_gst: Boolean(formData.price_inclusive_gst)
      };
      
      let response;
      if (editingProduct) {
        // Check multiple possible ID fields
        const productId =
          editingProduct.id || editingProduct.product_id || editingProduct._id;

        if (!productId) {
          setDialogError("Product ID is missing for update operation");
          return;
        }
         
        
        console.log("Updating product with ID:", productId);
        
        response = await apiService.put(`/products/${productId}`, submitData);

      } else {
        response = await apiService.post("/products", submitData);
      }

      if (response.success) {
        fetchData();
        handleCloseDialog();
        setSuccessMessage(editingProduct ? 'Product updated successfully!' : 'Product created successfully!');
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setDialogError(response.message || "Failed to save product");
      }
    } catch (err) {
      setDialogError("Error saving product: " + err.message);
    }
  };

  const handleMenuOpen = (event, product) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProduct(null);
  };

  const handleDeleteProduct = async () => {
    // Permission checks (UI-level). API also enforces this.
    if (!hasPermission("products", "delete")) {
      setError("Permission denied");
      handleMenuClose();
      return;
    }

    if (selectedProduct) {
      try {
        const response = await apiService.delete(
          `/products/${selectedProduct.id}`
        );
        if (response.success) {
          fetchData();
          setSuccessMessage('Product deleted successfully!');
          // Clear success message after 5 seconds
          setTimeout(() => setSuccessMessage(''), 5000);
        } else {
          setError("Failed to delete product");
        }
      } catch (err) {
        setError("Error deleting product: " + err.message);
      }
    }
    handleMenuClose();
  };

  const getBrand = (brandId) => {
    return brands.find((b) => b.id === brandId);
  };



  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Loading products...</Typography>
      </Box>
    );
  }

  return (
    <FeatureGuard feature="Product Management">
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          Products
        </Typography>
        <Box display="flex" gap={2}>
          {hasPermission("products", "add") && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add
          </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              fullWidth
              placeholder="Search products by name, brand, or description..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <SearchableDropdown
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { id: "all", name: "All Products", description: "Show all products" },
                { id: "active", name: "Active", description: "Show only active products" },
                { id: "inactive", name: "Inactive", description: "Show only inactive products" }
              ]}
              placeholder="Filter by status..."
              showDescription={true}
              searchable={false}
              clearValue="active"
              sx={{ minWidth: 200 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Products Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell  style={{ textAlign: "right" }}>Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box py={4}>
                    <Inventory
                      sx={{ fontSize: 64, color: "grey.400", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm || statusFilter !== "active"
                        ? "No products found matching your criteria"
                        : "No products yet"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {searchTerm || statusFilter !== "active"
                        ? "Try adjusting your search terms or filters"
                        : "Add your first product to get started"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => {
                const brand = getBrand(product.brand_id);

                return (
                  <TableRow key={product.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {product.name}
                        </Typography>
                        {product.description && (
                          <Typography variant="body2" color="text.secondary">
                            {product.description.length > 50
                              ? `${product.description.substring(0, 50)}...`
                              : product.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {brand ? brand.name : "Unknown Brand"}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ textAlign: "right" }}>
                      <Box display="flex" alignItems="center" justifyContent="flex-end"> 
                        <Typography variant="subtitle2" fontWeight="bold">
                          {formatPrice(product.price)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.status}
                        color={
                          product.status === STATUS_OPTIONS.ACTIVE
                            ? "success"
                            : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {(hasPermission("products", "edit") || hasPermission("products", "delete")) && (
                        <IconButton onClick={(e) => handleMenuOpen(e, product)}>
                          <MoreVert />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {hasPermission("products", "edit") && (
          <MenuItem
            onClick={() => {
              handleOpenDialog(selectedProduct);
              handleMenuClose();
            }}
          >
            <Edit sx={{ mr: 1 }} /> Edit
          </MenuItem>
        )}
        {hasPermission("products", "delete") && (
          <MenuItem onClick={handleDeleteProduct} sx={{ color: "error.main" }}>
            <Delete sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseDialog();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingProduct ? "Edit Product" : "Add New Product"}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setDialogError("")}
            >
              {dialogError}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            {/* Row 1: Product Name (full width) */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                sx={{ marginTop: "25px" }}
              /> 
              <SearchableDropdown
                label="Brand"
                value={formData.brand_id}
                onChange={(value) => setFormData({ ...formData, brand_id: value })}
                options={brands}
                placeholder="Search brands..."
                showDescription={true}
                showCount={true}
                clearValue={null}
              />
            </Box>

            {/* Row 3: Price + GST Percentage */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Price"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                required
                onWheel={(e) => e.target.blur()}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                        e.preventDefault();
                      }
                    }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {currencySymbol}
                    </InputAdornment>
                  ),
                }}
              />
               <TextField
                fullWidth
                label="GST Percentage (Optional)"
                name="gst_percentage"
                type="number"
                value={formData.gst_percentage}
                onChange={handleInputChange}
                placeholder="0.00"
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                helperText="Enter GST percentage if applicable"
              />
            </Box>

            {/* Row 4: Price Inclusive GST */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 200 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    id="price_inclusive_gst"
                    name="price_inclusive_gst"
                    checked={formData.price_inclusive_gst}
                    onChange={handleCheckboxChange}
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="price_inclusive_gst" style={{  color: 'rgba(0, 0, 0, 0.6)' }}>
                    Price is inclusive of GST
                  </label>
                </Box>
              </Box>
            </Box>



            {/* Row 5: Status (half width) + empty space */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <SearchableDropdown
                label="Status"
                value={formData.status}
                onChange={(value) => setFormData({ ...formData, status: value })}
                options={[
                  { id: STATUS_OPTIONS.ACTIVE, name: 'Active', description: 'Product is available for sale' },
                  { id: STATUS_OPTIONS.INACTIVE, name: 'Inactive', description: 'Product is not available for sale' }
                ]}
                placeholder="Select status..."
                showDescription={true}
                searchable={false}
                clearValue={STATUS_OPTIONS.ACTIVE}
              />
              <Box sx={{ width: "100%" }}></Box>
            </Box>

            {/* Row 6: Description (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe this product..."
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingProduct ? "Update" : "Add"} Product
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </FeatureGuard>
  );
};

export default Products;
